import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ReportTaxonomyField} from '../../contracts/report-taxonomy.types';
import {ResolverKind, ValueSource} from './valueSource.types';
import {createStaticValueSource} from './staticValueSources';
import {
	projectValueSource,
	personValueSource,
	clientValueSource,
	labelValueSource,
	projectLabelValueSource,
	roleValueSource,
	rateCardValueSource,
	clientContactValueSource,
	sprintValueSource,
} from './relayValueSources';
import {REMAINING_WORK_YES, REMAINING_WORK_NO} from '../remainingWorkCondition';
import {enumeratedCandidates, isSingleSelectEnumeration} from '../enumeratedValues';
import {ReportGrain} from '../../reportGrain';

const RELAY_REGISTRY: Partial<Record<ResolverKind, ValueSource>> = {
	project: projectValueSource,
	person: personValueSource,
	client: clientValueSource,
	label: labelValueSource,
	role: roleValueSource,
	rateCard: rateCardValueSource,
	clientContact: clientContactValueSource,
	sprint: sprintValueSource,
};

/** The kinds whose candidates arrive from a Relay fetch, so a tile showing a preset value has no
 * label for it until that fetch resolves. Derived from the registry rather than listed again, so a
 * kind registered here cannot be left out of the tile's label warm-up: that omission is what made
 * a reopened rate card or contact condition read "1 selected" instead of naming its value. */
export const RELAY_BACKED_KINDS: ResolverKind[] = Object.keys(RELAY_REGISTRY) as ResolverKind[];

/**
 * Resolves the `ValueSource` for a given resolver kind. `'boolean'` and `'workflowStatus'` are
 * built here (not in the static registry) because their candidate labels are user-facing and
 * must go through `useIntl`; the source is memoized on the formatted strings so identity stays
 * stable across renders unless the active locale's translations change. The relay-backed kinds
 * are plain registry lookups, except `'label'`, whose candidate pool differs by `grain` (see the
 * check ahead of the registry fallback below) because a task label and a project label are never
 * interchangeable.
 *
 * `'enumeratedValues'` is the one kind whose candidates are not fixed: they are the vocabulary the
 * taxonomy publishes on the field being edited, so the caller has to pass `field` alongside the
 * kind. Its labels are the catalog's own strings and are NOT run through `useIntl` - the same
 * treatment `field.label` already gets, and the only honest one, since translating a value the
 * picker has to write back verbatim would change what the condition stores.
 *
 * The workflow-status ids are status CATEGORIES, not status-column ids, because that is what the
 * status filter compares against server-side. Every project's own columns ("In Review", "QA",
 * "Blocked", ...) roll up into these three, so the list is complete and identical for every
 * company — a per-project status-name picker would be both unavailable company-wide and wrong on
 * the wire.
 */
/** A source for a value the reader types rather than picks. There is no candidate list to offer, so
 * whatever has been typed is presented as the single selectable candidate -- which lets an unbounded
 * value like a batch id be entered through the same checkbox picker every other condition uses,
 * rather than needing a second kind of control. */
const freeTextValueSource: ValueSource = {
	useCandidates(search: string) {
		const typed = search.trim();
		return {items: typed === '' ? [] : [{id: typed, label: typed}], loading: false};
	},
	resolveLabels(ids: string[]) {
		return Object.fromEntries(ids.map(id => [id, id]));
	},
};

export function useValueSource(kind: ResolverKind, field?: ReportTaxonomyField, grain?: ReportGrain | null): ValueSource {
	const {formatMessage} = useIntl();

	const yes = formatMessage({id: 'common.yes', defaultMessage: 'Yes'});
	const no = formatMessage({id: 'common.no', defaultMessage: 'No'});
	const todo = formatMessage({id: 'ai_report_builder.conditions.status_todo', defaultMessage: 'To Do'});
	const inProgress = formatMessage({id: 'common.in_progress', defaultMessage: 'In Progress'});
	const done = formatMessage({id: 'common.done', defaultMessage: 'Done'});

	// singleSelect: Yes and No are two readings of one comparison, not two values that can both
	// hold. Without it, ticking both wrote `values: ['true','false']`, which `operatorForValues`
	// then promoted to IN - an operator none of the boolean filter fields (BLOCKED, BUG,
	// HIGH_PRIORITY) declares, so the report-data fetch failed validation outright.
	const booleanSource = useMemo(
		() => ({
			...createStaticValueSource([
				{id: 'true', label: yes},
				{id: 'false', label: no},
			]),
			singleSelect: true,
		}),
		[yes, no]
	);

	// `echoUnknownIds` because these three are the complete status vocabulary: anything else stored
	// on a status condition is a stale value (e.g. a status name written before the filter's value
	// vocabulary was settled). Showing it as-is keeps the summary honest — without it the condition
	// would collapse to "N selected" and the bad value would be invisible.
	const workflowStatusSource = useMemo(
		() =>
			createStaticValueSource(
				[
					{id: 'TODO', label: todo},
					{id: 'INPROGRESS', label: inProgress},
					{id: 'DONE', label: done},
				],
				{echoUnknownIds: true}
			),
		[todo, inProgress, done]
	);

	// singleSelect: the two candidates are mutually exclusive readings of one comparison ("has work
	// left" vs "has none"), not a set of values that can coexist, so picking one replaces the other
	// rather than adding to it.
	const remainingWorkSource = useMemo(
		() => ({
			...createStaticValueSource([
				{id: REMAINING_WORK_YES, label: yes},
				{id: REMAINING_WORK_NO, label: no},
			]),
			singleSelect: true,
		}),
		[yes, no]
	);

	// Memoised on the field so the source keeps a stable identity across renders; the taxonomy is
	// held in state and its field objects are not rebuilt, so this recomputes only when the taxonomy
	// itself is replaced. `echoUnknownIds` because the declared list is this field's COMPLETE
	// vocabulary: an id outside it can only be a stale or agent-authored value, and showing it is
	// what stops the tile collapsing to "N selected" and hiding it.
	const enumeratedSource = useMemo(() => {
		if (!field) {
			return createStaticValueSource([]);
		}
		return {
			...createStaticValueSource(enumeratedCandidates(field, formatMessage), {echoUnknownIds: true}),
			singleSelect: isSingleSelectEnumeration(field),
		};
	}, [field, formatMessage]);

	if (kind === 'freeText') {
		return freeTextValueSource;
	}

	if (kind === 'enumeratedValues') {
		return enumeratedSource;
	}
	if (kind === 'boolean') {
		return booleanSource;
	}
	if (kind === 'workflowStatus') {
		return workflowStatusSource;
	}
	if (kind === 'remainingWork') {
		return remainingWorkSource;
	}
	// Labels are the one relay-backed kind whose candidate POOL differs by grain: a task label can
	// never match a project row and vice versa, so offering the wrong set gives a picker full of
	// options that cannot match anything. Every other relay kind (project, person, client, role) is
	// company-wide and identical on both grains, which is why only this one is special-cased.
	if (kind === 'label') {
		return grain === 'PROJECT_STATUS' ? projectLabelValueSource : labelValueSource;
	}
	// Falls back to an empty static source for any kind not in the relay registry, so a picker
	// never calls `.useCandidates`/`.resolveLabels` on `undefined` (e.g. an unrecognised resolver
	// kind arriving from the taxonomy).
	return RELAY_REGISTRY[kind] ?? createStaticValueSource([]);
}
